"use client";

import { useEffect, useRef } from "react";

const vertexShaderSource = `
  attribute vec2 a_position;

  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`;

const fragmentShaderSource = `
  precision highp float;

  uniform vec2 u_resolution;
  uniform float u_cell_size;
  uniform vec3 u_color;
  uniform float u_alpha;

  float bayer(vec2 position) {
    int x = int(mod(position.x, 8.0));
    int y = int(mod(position.y, 8.0));
    int index = y * 8 + x;
    int value;

    if(index==0) value=0;   else if(index==1) value=32;  else if(index==2) value=8;   else if(index==3) value=40;
    else if(index==4) value=2;   else if(index==5) value=34;  else if(index==6) value=10;  else if(index==7) value=42;
    else if(index==8) value=48;  else if(index==9) value=16;  else if(index==10) value=56; else if(index==11) value=24;
    else if(index==12) value=50; else if(index==13) value=18; else if(index==14) value=58; else if(index==15) value=26;
    else if(index==16) value=12; else if(index==17) value=44; else if(index==18) value=4;  else if(index==19) value=36;
    else if(index==20) value=14; else if(index==21) value=46; else if(index==22) value=6;  else if(index==23) value=38;
    else if(index==24) value=60; else if(index==25) value=28; else if(index==26) value=52; else if(index==27) value=20;
    else if(index==28) value=62; else if(index==29) value=30; else if(index==30) value=54; else if(index==31) value=22;
    else if(index==32) value=3;  else if(index==33) value=35; else if(index==34) value=11; else if(index==35) value=43;
    else if(index==36) value=1;  else if(index==37) value=33; else if(index==38) value=9;  else if(index==39) value=41;
    else if(index==40) value=51; else if(index==41) value=19; else if(index==42) value=59; else if(index==43) value=27;
    else if(index==44) value=49; else if(index==45) value=17; else if(index==46) value=57; else if(index==47) value=25;
    else if(index==48) value=15; else if(index==49) value=47; else if(index==50) value=7;  else if(index==51) value=39;
    else if(index==52) value=13; else if(index==53) value=45; else if(index==54) value=5;  else if(index==55) value=37;
    else if(index==56) value=63; else if(index==57) value=31; else if(index==58) value=55; else if(index==59) value=23;
    else if(index==60) value=61; else if(index==61) value=29; else if(index==62) value=53; else value=21;

    return float(value) / 64.0;
  }

  void main() {
    vec2 pixel = gl_FragCoord.xy;
    pixel.y = u_resolution.y - pixel.y;
    vec2 position = pixel / u_resolution;
    vec2 cell = floor(pixel / u_cell_size);
    float threshold = bayer(cell);

    vec2 left_corner = vec2(-0.05, 1.1);
    vec2 right_corner = vec2(1.05, 1.1);
    vec2 corner_scale = vec2(0.45, 0.95);
    float left_radius = length((position - left_corner) / corner_scale);
    float right_radius = length((position - right_corner) / corner_scale);
    float left_density = 1.0 - smoothstep(0.3, 1.05, left_radius);
    float right_density = 1.0 - smoothstep(0.3, 1.05, right_radius);

    vec2 center = vec2(0.5, 1.2);
    float center_radius = length((position - center) / vec2(0.8, 0.55));
    float center_density =
      (1.0 - smoothstep(0.2, 1.0, center_radius)) * 0.35;
    float density = max(max(left_density, right_density), center_density);

    if (density <= threshold) {
      discard;
    }

    vec2 cell_pixel =
      mod(pixel, u_cell_size) - u_cell_size * 0.5;
    float distance_to_center = length(cell_pixel);
    float dot_radius = u_cell_size * 0.28;
    float antialias = 1.0 - smoothstep(
      dot_radius - 0.5,
      dot_radius + 0.5,
      distance_to_center
    );

    if (antialias <= 0.0) {
      discard;
    }

    gl_FragColor = vec4(u_color, antialias * u_alpha);
  }
`;

function compileShader(
  context: WebGLRenderingContext,
  type: number,
  source: string,
) {
  const shader = context.createShader(type);

  if (!shader) {
    return null;
  }

  context.shaderSource(shader, source);
  context.compileShader(shader);

  if (!context.getShaderParameter(shader, context.COMPILE_STATUS)) {
    context.deleteShader(shader);
    return null;
  }

  return shader;
}

export function HeroDots() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("webgl", {
      alpha: true,
      premultipliedAlpha: true,
      antialias: true,
    });

    if (!canvas || !context) {
      return;
    }

    const vertexShader = compileShader(
      context,
      context.VERTEX_SHADER,
      vertexShaderSource,
    );
    const fragmentShader = compileShader(
      context,
      context.FRAGMENT_SHADER,
      fragmentShaderSource,
    );

    if (!vertexShader || !fragmentShader) {
      return;
    }

    const program = context.createProgram();
    const buffer = context.createBuffer();

    if (!program || !buffer) {
      return;
    }

    context.attachShader(program, vertexShader);
    context.attachShader(program, fragmentShader);
    context.linkProgram(program);

    if (!context.getProgramParameter(program, context.LINK_STATUS)) {
      context.deleteProgram(program);
      return;
    }

    context.useProgram(program);
    context.bindBuffer(context.ARRAY_BUFFER, buffer);
    context.bufferData(
      context.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      context.STATIC_DRAW,
    );

    const position = context.getAttribLocation(program, "a_position");
    const resolution = context.getUniformLocation(program, "u_resolution");
    const cellSize = context.getUniformLocation(program, "u_cell_size");
    const color = context.getUniformLocation(program, "u_color");
    const alpha = context.getUniformLocation(program, "u_alpha");

    context.enableVertexAttribArray(position);
    context.vertexAttribPointer(position, 2, context.FLOAT, false, 0, 0);
    context.enable(context.BLEND);
    context.blendFunc(context.SRC_ALPHA, context.ONE_MINUS_SRC_ALPHA);

    const draw = () => {
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.max(1, Math.floor(canvas.clientWidth * pixelRatio));
      const height = Math.max(1, Math.floor(canvas.clientHeight * pixelRatio));

      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        context.viewport(0, 0, width, height);
      }

      context.uniform2f(resolution, width, height);
      context.uniform1f(cellSize, 6 * pixelRatio);
      context.uniform3f(color, 198 / 255, 191 / 255, 56 / 255);
      context.uniform1f(alpha, 0.6);
      context.clearColor(0, 0, 0, 0);
      context.clear(context.COLOR_BUFFER_BIT);
      context.drawArrays(context.TRIANGLES, 0, 6);
    };

    const resizeObserver = new ResizeObserver(draw);
    resizeObserver.observe(canvas);
    window.addEventListener("resize", draw);
    draw();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", draw);
      context.deleteBuffer(buffer);
      context.deleteProgram(program);
      context.deleteShader(vertexShader);
      context.deleteShader(fragmentShader);
    };
  }, []);

  return <canvas ref={canvasRef} className="hero-dots" aria-hidden="true" />;
}
